// @ts-strict-ignore
import {AnyAttrs, AttrsSchema, Component} from 'panel';
import {nanoid} from 'nanoid';

import {ESCAPE_KEY_CODE, eventWasOutside, rafRetry, TAB_KEY_CODE} from 'common/util/dom';
import {getActiveElement, getFocusableChildren} from 'common/util/focusable';
import {assertUnreachable} from 'common/util/typescript';

export enum Visibility {
  Closed = `closed`,
  Open = `open`,
}

export type VisibilityChangeEvent = CustomEvent<{
  state: Visibility;
}>;

export const VisibilityEvents: {[x in Visibility]: string} = {
  [Visibility.Closed]: `stackable-closed`,
  [Visibility.Open]: `stackable-open`,
};

interface BaseState {
  visibility: Visibility;
}

interface StackableComponentAttrs {
  'prevent-close-selectors': string[];
  'close-of-same-type': boolean;
}

export const STACKABLE_CONTEXT_ATTR = `data-stackable-context`;

// elements that increase the stacking context
const STACKABLE_ELEMENTS = [
  `mp-create-entity-modal`,
  `mp-custom-event-modal`,
  `mp-sharing-modal`,
  `mp-custom-property-modal`,
  `mp-drop-menu`,
  `mp-modal`,
  `mp-workspace-leave-modal`,
  // should we just add `#modals` here to capture modals created by react-modal-promise#create?
  `.inline-cohorts-modal-wrapper`,
  `[${STACKABLE_CONTEXT_ATTR}]`,
];

const STACKABLE_ELEMENTS_SELECTOR = STACKABLE_ELEMENTS.join(`,`);

/**
 * StackableComponent defines the basis for how stacking elements should behave within a given stacking
 * context (e.g. at the body level, a dropdown within a dropdown, a dropdown within a modal, etc...)
 *
 * This component also keeps an internal state of the current stacking context
 */
export abstract class StackableComponent<
  T = UnknownObj,
  Attrs = AnyAttrs,
  ContextRegistry = UnknownObj,
> extends Component<BaseState & T, Attrs & StackableComponentAttrs, null, null, ContextRegistry> {
  static contextStack = new Set<string>();
  private declare stackingContext: number;
  public declare stackId: string;
  protected _deferChangeEvent = true;

  static get attrsSchema(): AttrsSchema<AnyAttrs> {
    return {
      'prevent-close-selectors': {
        description: `selectors that prevent closing the current stackable element e.g ['#help-button', '.widget-hover-fill']`,
        type: `json`,
        default: [],
      },
      'close-of-same-type': {
        description: `Only close previously opened stackables of the same type when opening a new one`,
        type: `boolean`,
      },
    };
  }

  /**
   * Sets the current stacking context target to a given element id
   */
  static setCurrentStackingContextTarget(id: string) {
    StackableComponent.contextStack.add(id);
  }

  /**
   * Removes the stacking context target from a given element id
   */
  static deleteCurrentStackingContextTarget(id: string) {
    StackableComponent.contextStack.delete(id);
  }

  /**
   * Checks if a given element id is the current stacking context target
   */
  static isCurrentStackingContextTarget(id: string) {
    return Array.from(StackableComponent.contextStack).pop() === id;
  }

  /**
   * Gets the depth of the current stacking context
   */
  static getCurrentStackingContextDepth() {
    return StackableComponent.contextStack.size;
  }

  /**
   * Checks what the stacking context depth would be for the given element
   */
  static getStackingContextDepthForElement(el: Element) {
    if (!el) {
      return 0;
    }
    const parent = el.parentElement || (el.getRootNode && (el.getRootNode() as ShadowRoot))?.host;
    return (
      (el.matches && el.matches(STACKABLE_ELEMENTS_SELECTOR) ? 1 : 0) +
      StackableComponent.getStackingContextDepthForElement(parent)
    );
  }

  constructor() {
    super();

    this.stackId = nanoid();
    this.handleWindowKeydown = this.handleWindowKeydown.bind(this);
    this.onStackableOpen = this.onStackableOpen.bind(this);
    this.mousedownOutsideListener = this.mousedownOutsideListener.bind(this);
  }

  get config() {
    return {
      ...super.config,
      defaultState: {
        ...super.config.defaultState,
        visibility: Visibility.Closed,
      },
    };
  }

  get isOpen() {
    return this.state.visibility === Visibility.Open;
  }

  get trapTab() {
    return true;
  }

  get shouldCloseWhenClickedOutside() {
    return true;
  }

  get shouldCloseOnEsc() {
    return true;
  }

  get increaseStackingContext() {
    return true;
  }

  get shouldAutoFocus() {
    return true;
  }

  connectedCallback() {
    super.connectedCallback();
    // Defer change events during connectedCallback so snabbdom can finish
    // attaching on={{ change }} listeners before the event fires.
    // The flag starts true and is cleared after the current microtask checkpoint.
    queueMicrotask(() => {
      this._deferChangeEvent = false;
    });
  }

  disconnectedCallback() {
    this._deferChangeEvent = true;
    this.onClose();
    super.disconnectedCallback();
  }

  protected onOpen() {
    if (this.shouldAutoFocus) {
      this.focusFirstChildren();
    }
    this.addKeyListeners();
  }

  protected onClose() {
    this.dispatchEvent(
      new CustomEvent(VisibilityEvents[Visibility.Closed], {
        bubbles: true,
        composed: true,
        detail: this.getStackingDetail(),
      }),
    );
    this.removeKeyListeners();
    StackableComponent.deleteCurrentStackingContextTarget(this.stackId);
    window.removeEventListener(VisibilityEvents[Visibility.Open], this.onStackableOpen);
  }

  private dispatchChange() {
    const event = new CustomEvent(`change`, {detail: {state: this.state.visibility}}) as VisibilityChangeEvent;
    if (this._deferChangeEvent) {
      queueMicrotask(() => this.dispatchEvent(event));
    } else {
      this.dispatchEvent(event);
    }
  }

  public getStackingDetail() {
    return {
      target: this.stackId,
      stackSize: StackableComponent.contextStack.size,
    };
  }

  protected setVisibility(visibility: Visibility) {
    this.update({visibility} as BaseState & T);
    this.increaseStackingContext &&
      this.dispatchEvent(
        new CustomEvent(VisibilityEvents[visibility], {
          bubbles: true,
          composed: true,
          detail: this.getStackingDetail(),
        }),
      );

    switch (visibility) {
      case Visibility.Open:
        this.dispatchChange();
        this.onOpen();
        break;
      case Visibility.Closed:
        this.dispatchChange();
        this.onClose();
        break;
      default:
        assertUnreachable(visibility);
        break;
    }
  }

  public isCurrentStackableTarget() {
    return StackableComponent.isCurrentStackingContextTarget(this.stackId);
  }

  public open() {
    switch (this.state.visibility) {
      case Visibility.Open:
        break;
      case Visibility.Closed:
        window.addEventListener(VisibilityEvents[Visibility.Open], this.onStackableOpen);
        this.setVisibility(Visibility.Open);
        break;
      default:
        assertUnreachable(this.state.visibility);
        break;
    }
  }

  public close() {
    switch (this.state.visibility) {
      case Visibility.Closed:
        break;
      case Visibility.Open:
        this.setVisibility(Visibility.Closed);
        break;
    }
  }

  protected isCloseable() {
    return true;
  }

  /**
   * When a stackable element opens within the same stacking context (e.g. 2 dropdowns inside a modal)
   * the desired behavior is to keep a single stackable element open at any given stacking context so that
   * we avoid issues like having 2+ opened dropdowns or modals alongside each other
   * If close-of-same-type is set to true, we only close the current stackable element if the type of
   * the newly opened stackable element is the same as the current one (e.g. opening a right drawer on a right drawer closes the previous one)
   */
  protected onStackableOpen(ev) {
    if (this.increaseStackingContext) {
      const {target} = ev.detail;
      const stackingContext = this.getStackingContextFromEvent(ev);
      const path = typeof ev.composedPath !== `function` ? ev.path : ev.composedPath();
      const selector = path[0];

      if (target === this.stackId) {
        this.stackingContext = stackingContext;
        StackableComponent.setCurrentStackingContextTarget(this.stackId);
      } else if (
        this.isOpen &&
        this.isCloseable() &&
        // keep all modals open, but close respective drop menus if there are multiple at once
        (StackableComponent.getCurrentStackingContextDepth() === 1 || selector.matches([`mp-drop-menu`]))
      ) {
        const modalType = selector?.getAttribute && selector.getAttribute(`modal-type`);
        if (this.attr(`close-of-same-type`)) {
          if (modalType === this.getAttribute(`modal-type`)) {
            this.close();
          }
        } else if (stackingContext === this.stackingContext) {
          this.close();
        }
      }
    }
  }

  /**
   * When a stackable element opens up, we fire an event that bubbles all the way through the root
   * document element, the stacking context returned here represents how many levels of stacking the
   * element being open up is within the page (e.g. the element is 2 modals deep, or 1 dropdown deep)
   */
  private getStackingContextFromEvent(ev) {
    const path = typeof ev.composedPath !== `function` ? ev.path : ev.composedPath();

    return path.reduce((context, el) => {
      return context + (el.matches && el.matches(STACKABLE_ELEMENTS_SELECTOR) ? 1 : 0);
    }, 0);
  }

  /**
   * Close current stackable element when clicked outside its target area, or when
   * clicking an element selector defined in attr(`prevent-close-selectors`)
   */
  private mousedownOutsideListener(ev: MouseEvent) {
    const clickWasOutside = eventWasOutside(ev, this, this.attr(`prevent-close-selectors`));

    if (this.isOpen && this.shouldCloseWhenClickedOutside && clickWasOutside && this.isCurrentStackableTarget()) {
      this.close();
    }
  }

  /**
   * Returns all focusable children in the light DOM tree or within the shadow root
   */
  protected getFocusableChildren() {
    return [...getFocusableChildren(this), ...(this.config.useShadowDom ? getFocusableChildren(this.el) : [])];
  }

  /**
   * Focus on first focusable child when the component is opened
   */
  private focusFirstChildren() {
    rafRetry(() => {
      const firstChild = this.getFocusableChildren().shift();
      if (firstChild) {
        firstChild.focus();
        return true;
      }
      return false;
    }).catch();
  }

  /**
   * Changes focus to next focusable child
   */
  private focusNextChildrenOnTab(ev: KeyboardEvent) {
    const children = this.getFocusableChildren();
    // If there are no focusable children, prevent tabbing through the rest
    // of the document before clearing focus on the dialog
    if (!children.length) {
      return ev.preventDefault();
    }

    // when pressing shift + tab, cycle backwards through the focusable element list
    const direction = ev.shiftKey ? -1 : 1;
    const focusedItemIndex = children.indexOf(getActiveElement(document) as HTMLElement);
    const lastChildIndex = children.length - 1;

    ev.preventDefault();
    if (focusedItemIndex === -1) {
      children[0].focus();
    } else {
      const nextElIndex = (focusedItemIndex + direction) % children.length;
      children[nextElIndex < 0 ? lastChildIndex : nextElIndex].focus();
    }
  }

  /**
   * Closes the current stackable element on ESC and focus on next children on TAB
   */
  private handleWindowKeydown(ev: KeyboardEvent) {
    if (!this.isOpen || !this.isCurrentStackableTarget()) {
      return;
    }

    switch (ev.keyCode) {
      case ESCAPE_KEY_CODE:
        if (this.shouldCloseOnEsc) {
          this.close();
        }
        break;
      case TAB_KEY_CODE:
        if (this.trapTab) {
          this.focusNextChildrenOnTab(ev);
        }
        break;
    }
  }

  private addKeyListeners() {
    window.addEventListener(`mousedown`, this.mousedownOutsideListener, true);
    document.addEventListener(`keydown`, this.handleWindowKeydown);
  }

  private removeKeyListeners() {
    window.removeEventListener(`mousedown`, this.mousedownOutsideListener, true);
    document.removeEventListener(`keydown`, this.handleWindowKeydown);
  }
}
